import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { useRef, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { billingAtom } from "../../../../../atoms/BillingAtom";
import upsellsAtom from "../../../../../atoms/UpsellsAtom";
import StyledButton from "../../../../StyledButton";
import { useModal } from "../../../Modal";
import BuySelectorModal from "../BuySelectorModal";
import { MonthSuffix, TotalDiv } from "./PaymentModalStyles";

const Container = styled.div<{ top: number }>`
  position: sticky;
  top: 50px;
  width: 450px;
  height: fit-content;
  background-color: #fff;
  border: 1px solid #eceeef;
  border-radius: 4px;
  padding: 50px 34px;
  font-family: "Source Sans Pro";
  color: #333333;
`;

const TitleContainer = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 17px;
`;

const SectionContainer = styled.div`
  margin-bottom: 17px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const Flex = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const SectionItemDescription = styled.div<{ noTier?: boolean }>`
  font-size: 16px;
  ${(props) => (props.noTier ? "font-style: italic;" : "")}
`;

const SectionItemPrice = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-align: right;
`;

const Separator = styled.div`
  border-bottom: 1px solid #eceeef;
  margin-bottom: 17px;
  height: 1px;
  width: 100%;
`;

const TotalTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const BigTotalDiv = styled(TotalDiv)`
  font-size: 30px;
`;

const DisclaimerDiv = styled.div`
  color: #707070;
  font-size: 13px;
  text-align: justify;
  margin: 21px 0px 27px 0px;
  line-height: 1.5;
`;

const SubmitButton = styled(StyledButton)`
  width: 100%;
  font-size: 20px;
  font-weight: 600;
`;

const TIER_TRANSLATION = {
  "-2": "Brawler (250)",
  "-1": "Scrapper (1000)",
  0: "Lout (2,500)",
  1: "Bruiser (5,000)",
  2: "Ruffian (10,000)",
  3: "Henchman (20,000)",
  4: "Savage (30,000)",
  5: "Barbarian (40,000)",
  6: "Belligerent (50,000)",
  7: "Berserker (75,000)",
  8: "Rabble-rouser (100,000)",
  9: "Warlord (125,000)",
};

interface ISummary {
  handleSubmit: () => Promise<boolean>;
}

export default function Summary(props: ISummary) {
  const { handleSubmit } = props;

  const [billingInfo, setBillingInfo] = useRecoilState(billingAtom);
  const [loading, setLoading] = useState(false);
  const modal = useModal();
  const setBoughtUpsells = useSetRecoilState(upsellsAtom);
  const router = useRouter();

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Container ref={containerRef} top={containerRef.current?.scrollTop || 0}>
      <TitleContainer>Summary</TitleContainer>
      <SectionContainer>
        <SectionTitle>Set Up</SectionTitle>
        <Flex>
          <SectionItemDescription>Initial Setup Fee</SectionItemDescription>
          <SectionItemPrice>
            {"$" + billingInfo.setupFeePrice || "--"}
          </SectionItemPrice>
        </Flex>
        <SectionTitle style={{ marginTop: "5px" }}>Tier</SectionTitle>
        <Flex>
          <SectionItemDescription noTier={billingInfo.tier === -999}>
            {TIER_TRANSLATION[billingInfo.tier]}
          </SectionItemDescription>
          <SectionItemPrice>
            $
            {billingInfo.basePrice
              ?.find((p) => p.tier === billingInfo.tier)
              ?.price.toFixed(0) || 0}
          </SectionItemPrice>
        </Flex>
      </SectionContainer>
      <SectionContainer>
        {(billingInfo.admins > 0 || billingInfo.externalUsers > 0) && (
          <SectionTitle>Additional</SectionTitle>
        )}
        {billingInfo.admins > 0 && (
          <>
            <Flex>
              <SectionItemDescription>
                Internal Users x {billingInfo.admins}
              </SectionItemDescription>
              <SectionItemPrice>
                ${(billingInfo.adminPrice || 10) * (billingInfo.admins || 0)}
              </SectionItemPrice>
            </Flex>
          </>
        )}
        {billingInfo.externalUsers > 0 && (
          <>
            <Flex>
              <SectionItemDescription>
                External Users x {billingInfo.externalUsers}
              </SectionItemDescription>
              <SectionItemPrice>
                $
                {(billingInfo.externalUserPrice || 5) *
                  (billingInfo.externalUsers || 0)}
              </SectionItemPrice>
            </Flex>
          </>
        )}
        {/* <SectionTitle>Total</SectionTitle> */}
        <Flex>
          <SectionItemDescription>
            <b>Total</b>
          </SectionItemDescription>
          <SectionItemPrice>
            {"$" +
              (
                (billingInfo.basePrice?.find((p) => p.tier === billingInfo.tier)
                  ?.price || 0) +
                billingInfo.additionalSum +
                billingInfo.setupFeePrice
              ).toFixed(0)}
          </SectionItemPrice>
        </Flex>
      </SectionContainer>
      <Separator />
      <Flex>
        <TotalTitle>To be billed monthly</TotalTitle>
        <BigTotalDiv>
          <span style={{ fontWeight: "normal", fontFamily: "Lato" }}>$</span>
          {(
            (billingInfo.basePrice?.find((p) => p.tier === billingInfo.tier)
              ?.price || 0) + billingInfo.additionalSum
          ).toFixed(0)}
          <MonthSuffix>/mo</MonthSuffix>
        </BigTotalDiv>
      </Flex>
      <DisclaimerDiv>
        By clicking the button below, I am acknowledging I have read, fully
        understand, and agree to be legally bound by the terms and conditions of
        the BerserkerMail Platform Subscription Agreement, and clicking the
        button below and providing my name as part of the purchasing process
        constitutes a valid and binding electronic signature for our agreement.
        <br />
        <br />
        Furthermore I understand that I can cancel my subscription to
        BerserkerMail at any time but there are{" "}
        <span style={{ backgroundColor: "yellow", fontWeight: "bold" }}>
          NO REFUNDS
        </span>{" "}
        on this or subsequent purchases including any digital training offers or
        1-click upsells.
      </DisclaimerDiv>
      <SubmitButton
        isLoading={loading}
        onClick={(e) => {
          e.preventDefault();
          (async () => {
            setLoading(true);
            const done = await handleSubmit();
            if (!done) {
              setLoading(false);
              return;
            }
            setLoading(false);
            setBoughtUpsells([
              {
                description:
                  "Subscription: " + TIER_TRANSLATION[billingInfo.tier],
                price:
                  (billingInfo.basePrice?.find(
                    (p) => p.tier === billingInfo.tier
                  )?.price || 0) * 100,
                uid: "Subscription",
              },
            ]);

            modal.pushModal(
              <BuySelectorModal
                onFinish={() => {
                  setBillingInfo((oldBillingInfo) => ({
                    ...oldBillingInfo,
                    update: true,
                  }));

                  router.push("/thank-you-purchase");
                }}
              />,
              "",
              true
            );
          })();
        }}
        disable={billingInfo.tier === -999}
      >
        Activate Account
      </SubmitButton>
    </Container>
  );
}
